// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { RolesService } from './../../../core/services/roles.service';
import { ActivatedRoute } from '@angular/router';
import { Input, Output, EventEmitter, Component, OnInit, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, zip, of, Subject } from 'rxjs';
import { filter, flatMap, map, takeUntil } from 'rxjs/operators';

import {
  LoadingScopeConstraintsAction,
  SelectScopeCategoryAction,
  SelectScopeConstraintsAction,
  selectRoleUserSelections,
  selectScopeConstraints,
  selectSelectedRoleScopeCategory,
  selectSelectedRoleScopeConstraints
} from '../../state';
import { DataTableAjaxComponent, SelectedRows } from '@app/shared/data-table-ajax/data-table-ajax.component';
import {
  MaybeLoadable,
  needsLoading,
  ScopeCategory,
  ScopeConstraint,
  Loadable,
  readParameterFromRouterState,
  Role,
  RoleObject,
  GoAction
} from '@app/core';
import { isScopeAdmin, disableMultiSelectRadio, disableSelectAllRadio } from '@app/core/utilities/scopes.helpers';
import { AlertService } from '@app/core/services/alert.service';
import { RoleDetailsFacadeService } from '@app/access-management/state/role-details.facade';

const shouldLoadCategories = (categories: MaybeLoadable) => needsLoading<ScopeCategory[]>(categories);

const shouldLoadConstraints = (constraints: MaybeLoadable) => needsLoading<ScopeConstraint[]>(constraints);

@Component({
  selector: 'app-role-scope-options',
  templateUrl: './role-scope-options.component.html',
  styleUrls: ['./role-scope-options.component.scss']
})
export class RoleScopeOptionsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() roleId!: string;
  @Input() backTo = ['/iam/roles', this.roleId];
  @Input() usernames = [];

  @Output() validityChange = new EventEmitter<boolean>(false);
  @Output() optionSelected = new EventEmitter<string>();
  @Output() saved = new EventEmitter<boolean>();

  @ViewChild('actionTemplate') actionTemplate: any;
  @ViewChild(DataTableAjaxComponent) dataTableAjax: DataTableAjaxComponent | undefined;
  @ViewChild('scopeMultipleRef') scopeMultipleRef: any;
  @ViewChild('scopeCatRef') scopeCatRef: any;
  @ViewChild('multiSelectRef') multiSelectRef: any;
  @ViewChild('selectAllRef') selectAllRef: any;
  @ViewChild('scopeCategoryRef') scopeCategoryRef: any;

  scopeCategories$: Observable<ScopeCategory[]>;
  scopeConstraints$!: Observable<Loadable | undefined>;
  selectedScopeCategory$!: Observable<ScopeCategory | undefined>;
  selectedScopeConstraints$!: Observable<ScopeConstraint[]>;
  categoryId = '0';

  columns: any[] = [];
  username: any;

  private loadConstraintsSubscription!: Subscription;
  roleUserScopes$: Observable<any>;
  userProjectScope$: Observable<any>;
  _roleId: any;
  _roleName: any;
  _category: ScopeCategory = null as any;
  userHasAdminScope$: Observable<boolean>;
  validatedCategories$: Observable<any>;
  selectedConstraints: any = [];
  selectedOrganizations: string[] = [];
  destroy$: Subject<void> = new Subject();

  useProjectCommitteeHandler = false;
  selectedUsers$: Observable<any> = of([]);

  constructor(
    private store: Store<any>,
    private route: ActivatedRoute,
    private rolesService: RolesService,
    private alertService: AlertService,
    private roleDetailsFacade: RoleDetailsFacadeService
  ) {
    this.username = readParameterFromRouterState(this.route, 'username');
    this._roleId = readParameterFromRouterState(this.route, 'id');
    this.scopeCategories$ = this.getFilteredRoleScopeCategories(this._roleId);
    this.roleDetailsFacade.initialize(this._roleId);
    this.roleDetailsFacade.role$.pipe(takeUntil(this.destroy$)).subscribe((role: any) => {
      this._roleName = role.name;
    });
    this.roleUserScopes$ = this.username
      ? this.rolesService
          .getRoleUserScopes(this._roleId, this.username)
          .pipe(map(list => [...list.map(row => ({ ...row, id: row.object_id }))]))
      : of([]);

    this.userHasAdminScope$ = this.roleUserScopes$.pipe(
      map(scopes => scopes.some((scope: any) => isScopeAdmin(scope.object_type_name)))
    );

    this.userProjectScope$ = this.roleUserScopes$.pipe(
      map(scopes =>
        scopes.map((scope: any) => {
          const projScope: any = scope;
          if (scope.id.split('|').length > 1 && scope.object_type_name === 'project|organization') {
            projScope.id = scope.id.split('|')[0];
          }
          return projScope;
        })
      )
    );

    this.validatedCategories$ = zip(this.scopeCategories$, this.userHasAdminScope$).pipe(
      map(responses => {
        const scopes = responses[0];
        const userHasAdminScope = responses[1];
        if (!userHasAdminScope) {
          return scopes;
        }
        const newScopes = scopes.map((scope: any) => {
          return {
            ...scope,
            disabled: isScopeAdmin(scope.name)
          };
        });
        return newScopes;
      })
    );
  }

  ngAfterViewInit() {
    this.columns = [
      {
        name: 'Name',
        prop: 'name',
        flexGrow: 2
      }
    ];
    this.scopeCatRef.nativeElement.hidden = true;
  }

  onRowClicked({ selected }: { [selected: string]: Role[] }) {}

  ngOnInit() {
    this.selectedScopeCategory$ = this.store.pipe(select(selectSelectedRoleScopeCategory(this.roleId)));

    const selectedCategory$ = this.filterMaybeSelectedCategory();
    this.scopeConstraints$ = this.selectConstraints$(selectedCategory$);
    this.loadConstraintsSubscription = this.loadSelectedCategoryConstraints(selectedCategory$);

    this.selectedScopeConstraints$ = this.store.pipe(select(selectSelectedRoleScopeConstraints(this.roleId)));

    this.selectedUsers$ = this.store.pipe(
      select(selectRoleUserSelections(this.roleId)),
      map(
        roleUserSelections =>
          (roleUserSelections &&
            roleUserSelections.users &&
            roleUserSelections.users.length &&
            roleUserSelections.users.map(user => user.username)) ||
          []
      )
    );
  }

  ngOnDestroy() {
    if (this.loadConstraintsSubscription) {
      this.loadConstraintsSubscription.unsubscribe();
    }
  }

  selectCategory(category: ScopeCategory) {
    this.optionSelected.emit(category.name.toLowerCase());
    if (category.name.toLowerCase() === 'project|committee') {
      this.useProjectCommitteeHandler = true;
      return;
    }

    this.useProjectCommitteeHandler = false;

    if (disableMultiSelectRadio(this._roleName, category.name)) {
      this.multiSelectRef.nativeElement.hidden = true;
      this.scopeCategoryRef.nativeElement.hidden = true;
      this.scopeCatRef.nativeElement.hidden = false;
      this.selectAllRef.nativeElement.hidden = false;
    } else if (this.scopeMultipleRef && !isScopeAdmin(category.name)) {
      this.scopeCatRef.nativeElement.hidden = false;
      this.multiSelectRef.nativeElement.hidden = false;
      this.scopeCategoryRef.nativeElement.hidden = false;
      if (category.name.toLowerCase() === 'project|organization') {
        this.categoryId = '1';
        this.scopeMultipleRef.nativeElement.hidden = false;
      } else {
        this.categoryId = category.id;
        this.scopeMultipleRef.nativeElement.hidden = true;
      }
      if (disableSelectAllRadio(this._roleName, category.name)) {
        this.selectAllRef.nativeElement.hidden = true;
      } else {
        this.selectAllRef.nativeElement.hidden = false;
      }
    } else {
      this.scopeCatRef.nativeElement.hidden = true;
    }
    this._category = category;
    this.store.dispatch(
      new SelectScopeCategoryAction({
        roleId: this.roleId,
        category
      })
    );
    this.allowSaveIfCategoryIsAdmin();
  }

  allowSaveIfCategoryIsAdmin() {
    this.validityChange.emit(this.isScopeAdmin());
  }

  isScopeAdmin() {
    return this._category && isScopeAdmin(this._category.name);
  }

  onSelectConstraints(row: SelectedRows) {
    const constraints = row.selected;
    if (this._category.name.toLowerCase() === 'organization' && row.selected.length > 1) {
      this.alertService.error('Error: Can select only one organization.');
    } else if (this._category.name.toLowerCase() === 'project|organization') {
      if (this.selectedOrganizations.length > 0) {
        this.selectedConstraints = row.selected;
        this.selectedConstraints.map((constraint: ScopeConstraint) => {
          const constraintArray = constraint.id.split('|');
          if (constraintArray.length > 1) {
            constraint.id = constraintArray[0];
          }
          constraint.id = constraint.id + '|' + this.selectedOrganizations[0];
        });
        this.selectConstraints(this.selectedConstraints);
      } else {
        this.selectedConstraints = row.selected;
        this.alertService.error('Please select organization');
      }
    } else {
      this.selectConstraints(constraints);
    }
  }

  onSelectMultipleConstraints(row: SelectedRows) {
    if (row.selected.length > 1) {
      this.alertService.error('Error: Can select only one organization.');
    } else if (row.selected.length === 1) {
      const orgaConstraint = row.selected[0];
      this.selectedOrganizations.length = 0;
      this.selectedOrganizations.push(orgaConstraint.id);
      this.selectedConstraints.map((constraint: ScopeConstraint) => {
        const constraintArray = constraint.id.split('|');
        if (constraintArray.length > 1) {
          constraint.id = constraintArray[0];
        }
        constraint.id = constraint.id + '|' + orgaConstraint.id;
      });
      this.selectConstraints(this.selectedConstraints);
    } else {
      this.alertService.error('Please select organization');
    }
  }

  selectConstraints(constraints: ScopeConstraint[]) {
    this.store.dispatch(
      new SelectScopeConstraintsAction({
        roleId: this.roleId,
        constraints
      })
    );
    this.validityChange.emit(constraints.length > 0);
  }

  onSelectAll(radioBtnEvent: any, scopeCategoryRef: any) {
    if (radioBtnEvent.target.checked) {
      scopeCategoryRef.hidden = true;
      const constraints: ScopeConstraint[] = [{ scopeId: '', id: '*', name: '' }];
      this.store.dispatch(
        new SelectScopeConstraintsAction({
          roleId: this.roleId,
          constraints
        })
      );
      this.validityChange.emit(constraints.length > 0);
    }
  }

  onSelectMultiple(radioBtnEvent: any, scopeCategoryRef: any) {
    if (radioBtnEvent.target.checked) {
      scopeCategoryRef.hidden = false;
    }
  }
  onSaved(value: boolean) {
    if (value) {
      this.store.dispatch(new GoAction(this.backTo));
    }
    this.saved.emit(value);
  }

  private loadSelectedCategoryConstraints(selectedScopeCategory$: Observable<ScopeCategory>) {
    const scopeConstraintsToLoad$: Observable<ScopeCategory> = this.scopeConstraints$.pipe(
      filter(shouldLoadConstraints),
      flatMap(() => selectedScopeCategory$)
    );
    return scopeConstraintsToLoad$.subscribe(category =>
      this.store.dispatch(new LoadingScopeConstraintsAction(category.id))
    );
  }

  private filterMaybeSelectedCategory(): Observable<ScopeConstraint> {
    return this.selectedScopeCategory$.pipe(filter<ScopeCategory>(maybeCategory => maybeCategory !== undefined));
  }

  private selectConstraints$(selectedCategory$: Observable<ScopeCategory>) {
    return selectedCategory$.pipe(flatMap(category => this.store.pipe(select(selectScopeConstraints(category.id)))));
  }

  private getFilteredRoleScopeCategories(roleId: string) {
    return this.rolesService.getRoleObjects(roleId).pipe(
      map(roleObjs =>
        roleObjs.map((role_obj: RoleObject) => {
          return { id: role_obj.object_id, name: role_obj.object_name } as ScopeCategory;
        })
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-scope-options/role-scope-options.component.ts